import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { FileText, Mail, Loader2, CalendarRange, History } from "lucide-react";
import { toast } from "sonner";
import Thinking from "@/components/Thinking";

const REPORT_STAGES = [
  "Gathering the week's signals",
  "Scoring adherence & load",
  "Summarising recovery & readiness",
  "Ranking goal progress",
  "Composing the report",
];

function ReportView({ r }) {
  return (
    <div className="mt-4 border border-border p-4" data-testid="report-view">
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="font-heading font-bold tracking-tight">{r.period === "week" ? "Weekly" : "Long-term (90-day)"} report</span>
        <span className="text-xs text-muted-foreground">{r.from} → {r.to}</span>
        {r.emailed && <span className="text-[10px] uppercase tracking-widest text-traffic-green border border-traffic-green/40 px-1.5 py-0.5">Emailed</span>}
      </div>
      {r.highlights?.length > 0 && (
        <ul className="list-disc pl-5 mt-3 space-y-1 text-sm">
          {r.highlights.map((h, i) => <li key={i} data-testid={`report-highlight-${i}`}>{h}</li>)}
        </ul>
      )}
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        {r.sections.map((s) => (
          <div key={s.title} className="border border-border">
            <div className="px-3 py-2 border-b border-border text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">{s.title}</div>
            {s.metrics.map((m, i) => (
              <div key={i} className="flex justify-between px-3 py-1.5 border-b border-border last:border-0 text-sm">
                <span className="text-muted-foreground">{m.label}</span>
                <span className="font-heading font-bold tabular-nums">{m.value == null ? "-" : m.value}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground mt-3">{r.disclaimer}</p>
    </div>
  );
}

export default function ReportsPanel({ athleteId }) {
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.get(`/athletes/${athleteId}/reports`).then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { loadHistory(); /* eslint-disable-next-line */ }, [athleteId]);

  const generate = async (period, email) => {
    setBusy(true); setCurrent(null);
    try {
      const { data } = await api.post(`/athletes/${athleteId}/reports?period=${period}&email=${email ? "true" : "false"}`);
      setCurrent(data);
      toast.success(`${period === "week" ? "Weekly" : "Long-term"} report ready${data.emailed ? " · emailed" : ""}`);
      loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate report"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border-b border-border p-8" data-testid="reports-panel">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <FileText className="w-4 h-4" />
        <span className="text-xs tracking-[0.2em] uppercase font-bold flex-1">Reports</span>
        <button data-testid="report-week-btn" onClick={() => generate("week", false)} disabled={busy}
          className="text-xs border border-foreground px-3 py-1.5 hover:bg-surface transition-colors flex items-center gap-1.5 disabled:opacity-40">
          <CalendarRange className="w-3.5 h-3.5" /> Current week
        </button>
        <button data-testid="report-long-btn" onClick={() => generate("long_term", false)} disabled={busy}
          className="text-xs border border-border px-3 py-1.5 hover:bg-surface transition-colors flex items-center gap-1.5 disabled:opacity-40">
          <History className="w-3.5 h-3.5" /> Long-term
        </button>
        <button data-testid="report-email-btn" onClick={() => generate("week", true)} disabled={busy}
          className="text-xs border border-border px-3 py-1.5 hover:bg-surface transition-colors flex items-center gap-1.5 disabled:opacity-40">
          <Mail className="w-3.5 h-3.5" /> Email weekly
        </button>
      </div>
      {busy && <Thinking label="Generating report" stages={REPORT_STAGES} />}
      {current && <ReportView r={current} />}
      {history.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">Recent reports</div>
          <div className="flex flex-wrap gap-2">
            {history.map((h) => (
              <button key={h.report_id} onClick={() => setCurrent(h)} data-testid={`report-history-${h.report_id}`}
                className="text-xs border border-border px-2.5 py-1 hover:bg-surface transition-colors">
                {h.period === "week" ? "Weekly" : "Long-term"} · {h.to}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
